import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Loader2, LogOut, Trash2, Laptop2, Smartphone, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const parseUserAgent = (ua = '') => {
  if (!ua) return { device: 'Unknown device', browser: '' };
  const mobile = /mobile|iphone|ipad|android/i.test(ua);
  let browser = 'Browser';
  if (/chrome/i.test(ua) && !/edg/i.test(ua)) browser = 'Chrome';
  else if (/safari/i.test(ua) && !/chrome/i.test(ua)) browser = 'Safari';
  else if (/firefox/i.test(ua)) browser = 'Firefox';
  else if (/edg/i.test(ua)) browser = 'Edge';
  return { device: mobile ? 'Mobile' : 'Desktop', browser };
};

const TrustedDevices = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/trusted-devices`);
      setDevices(res.data.devices || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/auth?redirect=/account/security', { replace: true });
        return;
      }
      toast.error(err?.response?.data?.detail || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    if (!window.confirm('Sign out of this device? You will need the email code next time.')) return;
    setRevoking(id);
    try {
      await axios.delete(`${API}/auth/trusted-devices/${id}`);
      toast.success('Device revoked');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke');
    } finally {
      setRevoking('');
    }
  };

  const revokeAll = async () => {
    if (!window.confirm('Sign out of ALL trusted devices? You will need the email code on every device.')) return;
    setRevoking('all');
    try {
      const res = await axios.delete(`${API}/auth/trusted-devices`);
      toast.success(`Revoked ${res.data.revoked} device(s)`);
      // Also drop local device token so THIS browser is untrusted too.
      localStorage.removeItem('dfl_device_token');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke all');
    } finally {
      setRevoking('');
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="trusted-devices-page">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="h-7 w-7 text-green-600" aria-hidden="true" />
        <div>
          <h1 className="text-2xl font-bold">Trusted Devices</h1>
          <p className="text-sm text-gray-500">
            Devices that skip email verification codes for 7 days after sign-in.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="p-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-green-600" aria-label="Loading devices" />
        </div>
      ) : devices.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" aria-hidden="true" />
            <p className="text-gray-500 text-sm">No trusted devices yet.</p>
            <p className="text-gray-400 text-xs mt-1">
              Tick &quot;Remember this device for 7 days&quot; during sign-in to add one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3 mb-6">
            {devices.map((d) => {
              const { device, browser } = parseUserAgent(d.user_agent);
              const Icon = device === 'Mobile' ? Smartphone : Laptop2;
              return (
                <Card key={d._id} data-testid={`device-row-${d._id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-green-700" aria-hidden="true" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">
                          {browser} on {device}
                        </p>
                        <p className="text-xs text-gray-500 truncate">IP {d.ip || 'unknown'}</p>
                        <p className="text-xs text-gray-400">
                          Added {new Date(d.created_at).toLocaleDateString()} · last used{' '}
                          {d.last_used_at ? new Date(d.last_used_at).toLocaleString() : '—'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                      onClick={() => revoke(d._id)}
                      disabled={revoking === d._id}
                      data-testid={`revoke-device-${d._id}`}
                      aria-label={`Revoke ${browser} on ${device}`}
                    >
                      {revoking === d._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="border-red-200 bg-red-50/40">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-red-900">Sign out of every device</p>
                <p className="text-red-700 text-xs">
                  If you suspect your account is compromised, revoke everywhere and change your password.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100"
                onClick={revokeAll}
                disabled={revoking === 'all'}
                data-testid="revoke-all-devices-btn"
              >
                {revoking === 'all' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogOut className="h-4 w-4 mr-2" />}
                Sign out everywhere
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TrustedDevices;
